/* eslint-disable sonarjs/no-duplicate-string */
/**
 * Tools for SVG sanitization.
 * @module sanitize
 * @license MIT
 *
 * @copyright 2010 Alexis Deveria, 2010 Jeff Schiller
 */

import { getReverseNS, NS } from '../common/namespaces';
import { isGecko } from '../common/browser';
import { getHref, setHref, getUrlFromAttr } from './utilities';

const REVERSE_NS = getReverseNS();

// Todo: Split out into core attributes, presentation attributes, etc. so consistent
/**
 * This defines which elements and attributes that we support (or at least
 * don't remove).
 * @type {PlainObject}
 */
/* eslint-disable max-len */
const svgWhiteList_ = {
    // SVG Elements
    a: ['class', 'title', 'clip-path', 'clip-rule', 'fill', 'fill-opacity', 'fill-rule', 'filter', 'id', 'mask', 'opacity', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'systemLanguage', 'transform', 'xlink:href', 'xlink:title'],
    circle: ['class', 'title', 'clip-path', 'clip-rule', 'cx', 'cy', 'fill', 'fill-opacity', 'fill-rule', 'filter', 'id', 'mask', 'opacity', 'r', 'requiredFeatures', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'systemLanguage', 'transform'],
    clipPath: ['class', 'title', 'clipPathUnits', 'id'],
    defs: [],
    style: ['type'],
    desc: [],
    ellipse: ['class', 'title', 'clip-path', 'clip-rule', 'cx', 'cy', 'fill', 'fill-opacity', 'fill-rule', 'filter', 'id', 'mask', 'opacity', 'requiredFeatures', 'rx', 'ry', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'systemLanguage', 'transform'],
    feBlend: ['in', 'in2'],
    feComposite: ['operator', 'result', 'in2'],
    feFlood: ['flood-color', 'in'],
    feGaussianBlur: ['class', 'title', 'color-interpolation-filters', 'id', 'requiredFeatures', 'stdDeviation'],
    feMerge: [],
    feMergeNode: ['in'],
    feMorphology: ['class', 'title', 'in', 'operator', 'radius'],
    feOffset: ['dx', 'in', 'dy', 'result'],
    filter: ['class', 'title', 'color-interpolation-filters', 'filterRes', 'filterUnits', 'height', 'id', 'primitiveUnits', 'requiredFeatures', 'width', 'x', 'xlink:href', 'y'],
    foreignObject: ['class', 'title', 'font-size', 'height', 'id', 'opacity', 'requiredFeatures', 'style', 'transform', 'width', 'x', 'y'],
    g: ['class', 'title', 'clip-path', 'clip-rule', 'id', 'display', 'fill', 'fill-opacity', 'fill-rule', 'filter', 'mask', 'opacity', 'requiredFeatures', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'systemLanguage', 'transform', 'font-family', 'font-size', 'font-style', 'font-weight', 'text-anchor'],
    image: ['class', 'title', 'clip-path', 'clip-rule', 'filter', 'height', 'id', 'mask', 'opacity', 'requiredFeatures', 'style', 'systemLanguage', 'transform', 'width', 'x', 'xlink:href', 'xlink:title', 'y'],
    line: ['class', 'title', 'clip-path', 'clip-rule', 'fill', 'fill-opacity', 'fill-rule', 'filter', 'id', 'marker-end', 'marker-mid', 'marker-start', 'mask', 'opacity', 'requiredFeatures', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'systemLanguage', 'transform', 'x1', 'x2', 'y1', 'y2'],
    linearGradient: ['class', 'title', 'id', 'gradientTransform', 'gradientUnits', 'requiredFeatures', 'spreadMethod', 'systemLanguage', 'x1', 'x2', 'xlink:href', 'y1', 'y2'],
    marker: ['id', 'class', 'title', 'markerHeight', 'markerUnits', 'markerWidth', 'orient', 'preserveAspectRatio', 'refX', 'refY', 'systemLanguage', 'viewBox'],
    mask: ['class', 'title', 'height', 'id', 'maskContentUnits', 'maskUnits', 'width', 'x', 'y'],
    metadata: ['class', 'title', 'id'],
    path: ['class', 'title', 'clip-path', 'clip-rule', 'd', 'fill', 'fill-opacity', 'fill-rule', 'filter', 'id', 'marker-end', 'marker-mid', 'marker-start', 'mask', 'opacity', 'requiredFeatures', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'systemLanguage', 'transform'],
    pattern: ['class', 'title', 'height', 'id', 'patternContentUnits', 'patternTransform', 'patternUnits', 'requiredFeatures', 'style', 'systemLanguage', 'viewBox', 'width', 'x', 'xlink:href', 'y'],
    polygon: ['class', 'title', 'clip-path', 'clip-rule', 'id', 'fill', 'fill-opacity', 'fill-rule', 'filter', 'marker-end', 'marker-mid', 'marker-start', 'mask', 'opacity', 'points', 'requiredFeatures', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'systemLanguage', 'transform'],
    polyline: ['class', 'title', 'clip-path', 'clip-rule', 'id', 'fill', 'fill-opacity', 'fill-rule', 'filter', 'marker-end', 'marker-mid', 'marker-start', 'mask', 'opacity', 'points', 'requiredFeatures', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'systemLanguage', 'transform'],
    radialGradient: ['class', 'title', 'cx', 'cy', 'fx', 'fy', 'gradientTransform', 'gradientUnits', 'id', 'r', 'requiredFeatures', 'spreadMethod', 'systemLanguage', 'xlink:href'],
    rect: ['class', 'title', 'clip-path', 'clip-rule', 'fill', 'fill-opacity', 'fill-rule', 'filter', 'height', 'id', 'mask', 'opacity', 'requiredFeatures', 'rx', 'ry', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'systemLanguage', 'transform', 'width', 'x', 'y'],
    stop: ['class', 'title', 'id', 'offset', 'requiredFeatures', 'stop-color', 'stop-opacity', 'style', 'systemLanguage'],
    svg: ['class', 'title', 'clip-path', 'clip-rule', 'enable-background', 'filter', 'id', 'height', 'mask', 'preserveAspectRatio', 'requiredFeatures', 'style', 'systemLanguage', 'version', 'viewBox', 'width', 'x', 'xmlns', 'xmlns:se', 'xmlns:xlink', 'xmlns:oi', 'oi:animations', 'y'],
    switch: ['class', 'title', 'id', 'requiredFeatures', 'systemLanguage'],
    symbol: ['class', 'title', 'fill', 'fill-opacity', 'fill-rule', 'filter', 'font-family', 'font-size', 'font-style', 'font-weight', 'id', 'opacity', 'preserveAspectRatio', 'requiredFeatures', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'systemLanguage', 'transform', 'viewBox'],
    text: ['class', 'title', 'clip-path', 'clip-rule', 'fill', 'fill-opacity', 'fill-rule', 'filter', 'font-family', 'font-size', 'font-style', 'font-weight', 'id', 'letter-spacing', 'mask', 'opacity', 'requiredFeatures', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'systemLanguage', 'text-anchor', 'transform', 'x', 'xml:space', 'y'],
    textPath: ['class', 'title', 'id', 'method', 'requiredFeatures', 'spacing', 'startOffset', 'style', 'systemLanguage', 'transform', 'xlink:href'],
    title: [],
    tspan: ['class', 'title', 'clip-path', 'clip-rule', 'dx', 'dy', 'fill', 'fill-opacity', 'fill-rule', 'filter', 'font-family', 'font-size', 'font-style', 'font-weight', 'id', 'letter-spacing', 'line-height', 'mask', 'opacity', 'requiredFeatures', 'rotate', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'systemLanguage', 'text-anchor', 'textLength', 'transform', 'x', 'xml:space', 'y'],
    use: ['class', 'title', 'clip-path', 'clip-rule', 'fill', 'fill-opacity', 'fill-rule', 'filter', 'height', 'href', 'id', 'letter-spacing', 'mask', 'overflow', 'stroke', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke-width', 'style', 'transform', 'width', 'x', 'xlink:href', 'y'],

    // MathML Elements
    annotation: ['encoding'],
    'annotation-xml': ['encoding'],
    maction: ['actiontype', 'other', 'selection'],
    math: ['class', 'id', 'display', 'xmlns'],
    menclose: ['notation'],
    merror: [],
    mfrac: ['linethickness'],
    mi: ['mathvariant'],
    mmultiscripts: [],
    mn: [],
    mo: ['fence', 'lspace', 'maxsize', 'minsize', 'rspace', 'stretchy'],
    mover: [],
    mpadded: ['lspace', 'width', 'height', 'depth', 'voffset'],
    mphantom: [],
    mprescripts: [],
    mroot: [],
    mrow: ['xlink:href', 'xlink:type', 'xmlns:xlink'],
    mspace: ['depth', 'height', 'width'],
    msqrt: [],
    mstyle: ['displaystyle', 'mathbackground', 'mathcolor', 'mathvariant', 'scriptlevel'],
    msub: [],
    msubsup: [],
    msup: [],
    mtable: ['align', 'columnalign', 'columnlines', 'columnspacing', 'displaystyle', 'equalcolumns', 'equalrows', 'frame', 'rowalign', 'rowlines', 'rowspacing', 'width'],
    mtd: ['columnalign', 'columnspan', 'rowalign', 'rowspan'],
    mtext: [],
    mtr: ['columnalign', 'rowalign'],
    munder: [],
    munderover: [],
    none: [],
    semantics: []
};
/* eslint-enable max-len */

// Produce a Namespace-aware version of svgWhitelist
type NSAttributeCollection = { [key: string]: Array<string | null> };
const svgWhiteListNS_: { [key: string]: NSAttributeCollection } = { };
const globallyAllowedNamespaces: string[] = [
    NS.XMLNS,
    NS.XML,
    NS.XLINK,
    NS.A,
    NS.ZAA,
];

Object.entries(svgWhiteList_).forEach(function ([elt, atts]) {
    const attNS: NSAttributeCollection = {};

    Object.entries(atts).forEach(function ([_i, att]) {
        if (att.includes(':')) {
            const v = att.split(':');
            const ns = NS[(v[0]).toUpperCase()];
            if (!attNS[v[1]]) {
                attNS[v[1]] = [null];
            }
            attNS[v[1]].push(ns);
        } else {
            if (!attNS[att]) {
                attNS[att] = [null];
            }
            if (att === 'xmlns') {
                attNS[att].push(NS.XMLNS);
            }
        }
    });

    svgWhiteListNS_[elt] = attNS;
});

const svgWhiteListPrefixes_ = ['pd-', 'pe-'];

// Search regex: new\((.*?), SvgNamespaces\.ZAA, (.*?)\),
// Replace regex: 
const attributeRemappings = {
    "pe-version": "zaa:version",
    "pe-dpi": "zaa:dpi",
    "pe-doc-notes": "zaa:document-notes",
    "pe-expand-width": "zaa:allow-expand-width",
    "pe-expand-height": "zaa:allow-expand-height",
    "pe-is-implicit-component": "zaa:document-is-implicit-component",
    "pe-impcomp-dir": "zaa:document-implicit-component-direction",
    "pe-impcomp-padding": "zaa:document-implicit-component-padding",

    "pe-tag": "zaa:component-tag",
    "pe-tag-content": "zaa:content-tag",
    "pe-tag-fill": "zaa:fill-tag",
    "pe-tag-stroke": "zaa:stroke-tag",

    "pe-component": "zaa:component-reference",
    "pe-repeat-limit": "zaa:component-repeat-limit",
    "pe-repeat-cols-first": "zaa:component-repeat-columns-first",
    "pe-repeat-invert-dir": "zaa:component-repeat-invert-direction",
    "pe-repeat-padding-cols": "zaa:component-repeat-column-gap",
    "pe-repeat-padding-rows": "zaa:component-repeat-row-gap",
    "pe-repeat-doc-padding-right": "zaa:element-document-margin-right",
    "pe-repeat-doc-padding-bottom": "zaa:element-document-margin-bottom",
    "pe-comp-rules": "zaa:component-rules",
};

/**
* Sanitizes the input node and its children.
* It only keeps what is allowed from our whitelist defined above.
* @function module:sanitize.sanitizeSvg
* @param {Text|Element} node - The DOM element to be checked (we'll also check its children) or text node to be cleaned up
* @returns {void}
*/
export const sanitizeSvg = function (node) {
    // Cleanup text nodes
    if (node.nodeType === Node.TEXT_NODE) {
        // Trim whitespace
        node.nodeValue = node.nodeValue.replace(/(?:^\s+|\s+$)/g, '');
        // Remove if empty
        if (!node.nodeValue.length) {
            node.remove();
        }
    }

    // We only care about element nodes.
    // Automatically return for all non-element nodes, such as comments, etc.
    if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
    }

    const doc = node.ownerDocument;
    const parent = node.parentNode;
    // can parent ever be null here?  I think the root node's parent is the document...
    if (!doc || !parent) {
        return;
    }

    // Replace any node attributes with name remappings
    for (const [attr, newAttr] of Object.entries(attributeRemappings)) {
        if (node.hasAttribute(attr)) {
            const value = node.getAttribute(attr);
            node.removeAttribute(attr);
            node.setAttribute(newAttr, value);
        }
    }

    const allowedAttrs = svgWhiteList_[node.nodeName];
    const allowedAttrsNS = svgWhiteListNS_[node.nodeName];
    // if this element is supported, sanitize it
    if (typeof allowedAttrs !== 'undefined') {
        const seAttrs = [];
        let i = node.attributes.length;
        while (i--) {
            // if the attribute is not in our whitelist, then remove it
            // could use jQuery's inArray(), but I don't know if that's any better
            const attr = node.attributes.item(i);
            const attrName = attr.nodeName;
            const attrLocalName = attr.localName;
            const attrNsURI = attr.namespaceURI;

            let isValid = globallyAllowedNamespaces.includes(attrNsURI);
            if(!isValid) {
                const check1 = {}.hasOwnProperty.call(allowedAttrsNS, attrLocalName)
                    && allowedAttrsNS[attrLocalName].some(ns => ns === attrNsURI)
                    && attrNsURI !== NS.XMLNS;
                const check2 = attrNsURI === NS.XMLNS && REVERSE_NS[attr.value];

                isValid = check1 && !check2;
            }

            if (!isValid) {
                for (const prefix of svgWhiteListPrefixes_) {
                    if (attrName.startsWith(prefix)) {
                        isValid = true;
                        break;
                    }
                }
            }

            // Check that an attribute with the correct localName in the correct namespace is on
            // our whitelist or is a namespace declaration for one of our allowed namespaces
            if (!isValid) {
                // TODO(codedread): Programmatically add the se: attributes to the NS-aware whitelist.
                // Bypassing the whitelist to allow se: prefixes.
                // Is there a more appropriate way to do this?
                if (attrName.startsWith('se:') || attrName.startsWith('data-')) {
                    seAttrs.push([attrName, attr.value]);
                }

                console.log(`[Sanitize] Removing attr ${attrName} from element ${node.nodeName} with namespace '${attrNsURI}' - local name '${attrLocalName}'`);
                node.removeAttributeNS(attrNsURI, attrLocalName);
            }

            // Add spaces before negative signs where necessary
            if (isGecko()) {
                switch (attrName) {
                    case 'transform':
                    case 'gradientTransform':
                    case 'patternTransform': {
                        const val = attr.value.replace(/(\d)-/g, '$1 -');
                        node.setAttribute(attrName, val);
                        break;
                    }
                }
            }

            // For the style attribute, rewrite it in terms of XML presentational attributes
            if (attrName === 'style') {
                const props = attr.value.split(';');
                let p = props.length;
                while (p--) {
                    const [name, val] = props[p].split(':');
                    const styleAttrName = (name || '').trim();
                    const styleAttrVal = (val || '').trim();
                    // Now check that this attribute is supported
                    if (allowedAttrs.includes(styleAttrName)) {
                        node.setAttribute(styleAttrName, styleAttrVal);
                    }
                }
                node.removeAttribute('style');
            }
        }

        Object.values(seAttrs).forEach(([att, val]) => {
            node.setAttributeNS(NS.SE, att, val);
        });

        // for some elements that have a xlink:href, ensure the URI refers to a local element
        // (but not for links)
        const href = getHref(node);
        if (href && ['filter', 'linearGradient', 'pattern', 'radialGradient', 'textPath', 'use'].includes(node.nodeName) && !href.startsWith('#')) {
            // remove the attribute (but keep the element)
            setHref(node, '');
            node.removeAttributeNS(NS.XLINK, 'href');
        }

        // <use> needs a valid `href` or `xlink:href`
        if (node.nodeName === 'use' && !getHref(node)) {
            node.remove();
            return;
        }

        // if the element has attributes pointing to a non-local reference,
        // need to remove the attribute
        for (const attr of ['clip-path', 'fill', 'filter', 'marker-end', 'marker-mid', 'marker-start', 'mask', 'stroke']) {
            let val = node.getAttribute(attr);
            if (val) {
                val = getUrlFromAttr(val);
                // simply check for first character being a '#'
                if (val && val[0] !== '#') {
                    node.setAttribute(attr, '');
                    node.removeAttribute(attr);
                }
            }
        }

        if (node.nodeName.toLowerCase() !== "metadata") {
            // recurse to children
            i = node.childNodes.length;
            while (i--) {
                sanitizeSvg(node.childNodes.item(i));
            }
        }
        // else (element not supported), remove it
    } else {
        // remove all children from this node and insert them before this node
        // TODO: in the case of animation elements this will hardly ever be correct
        const children = [];
        while (node.hasChildNodes()) {
            children.push(parent.insertBefore(node.firstChild, node));
        }

        // remove this node from the document altogether
        node.remove();

        // call sanitizeSvg on each of those children
        let i = children.length;
        while (i--) {
            sanitizeSvg(children[i]);
        }
    }
};
